import { apiClient } from './request';
import { create, fromJson, type JsonValue, toJson } from '@bufbuild/protobuf';
import { type Agent, AgentSchema } from '@/proto/agentplatform/v1/agent_pb';
import {
  CreateAgentRequestSchema,
  CreateAgentResponseSchema,
  GetAgentRequestSchema,
  GetAgentResponseSchema,
  UpdateAgentRequestSchema,
  UpdateAgentResponseSchema,
  DeleteAgentRequestSchema,
  DeleteAgentResponseSchema,
  ListAgentsResponseSchema,
  RestartAgentRequestSchema,
  RestartAgentResponseSchema,
  ListInternalToolkitsResponseSchema,
  type InternalToolkit,
} from '@/proto/agentplatform/httpv2/agent_server_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';

/** 列表返回的通用形状，兼容页面既有的 `data.data` / `data.total` 读法。 */
export interface AgentListResult {
  data: Agent[];
  total: number;
}

export interface InternalToolkitListResult {
  data: InternalToolkit[];
}

export const agentApi = {
  listAgents: async (params: ListRequest): Promise<AgentListResult> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListAgents',
      toJson(ListRequestSchema, params)
    );
    const resp = fromJson(ListAgentsResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  getAgent: async (id: string): Promise<Agent> => {
    const req = create(GetAgentRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/GetAgent',
      toJson(GetAgentRequestSchema, req)
    );
    const resp = fromJson(GetAgentResponseSchema, responseObject.data);
    return resp.data ?? create(AgentSchema, {});
  },

  createAgent: async (data: Agent): Promise<Agent> => {
    const req = create(CreateAgentRequestSchema, {
      name: data.name,
      description: data.description,
      modelConfig: data.modelConfig,
      channelConfigs: data.channelConfigs,
      pluginConfigs: data.pluginConfigs,
      advancedConfig: data.advancedConfig,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/CreateAgent',
      toJson(CreateAgentRequestSchema, req)
    );
    const resp = fromJson(CreateAgentResponseSchema, responseObject.data);
    return resp.data ?? create(AgentSchema, {});
  },

  updateAgent: async (id: string, data: Agent): Promise<Agent> => {
    const req = create(UpdateAgentRequestSchema, {
      id,
      name: data.name,
      description: data.description,
      modelConfig: data.modelConfig,
      channelConfigs: data.channelConfigs,
      pluginConfigs: data.pluginConfigs,
      advancedConfig: data.advancedConfig,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/UpdateAgent',
      toJson(UpdateAgentRequestSchema, req)
    );
    const resp = fromJson(UpdateAgentResponseSchema, responseObject.data);
    return resp.data ?? create(AgentSchema, {});
  },

  deleteAgent: async (id: string): Promise<void> => {
    const req = create(DeleteAgentRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/DeleteAgent',
      toJson(DeleteAgentRequestSchema, req)
    );
    fromJson(DeleteAgentResponseSchema, responseObject.data);
  },

  restartAgent: async (id: string): Promise<void> => {
    const req = create(RestartAgentRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/RestartAgent',
      toJson(RestartAgentRequestSchema, req)
    );
    // ignoreUnknownFields: 兼容后端 envelope 增字段，避免 HTTP 200 却因 fromJson 抛错被当成失败
    fromJson(RestartAgentResponseSchema, responseObject.data, {
      ignoreUnknownFields: true,
    });
  },

  listInternalToolkits: async (): Promise<InternalToolkitListResult> => {
    // 该 RPC 入参为 google.protobuf.Empty，无需分页/过滤；POST 空体即可。
    const responseObject = await apiClient.post<JsonValue>('/v2/ListInternalToolkits', {});
    const resp = fromJson(ListInternalToolkitsResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [] };
  },
};
